import { Button } from '@/lib/Buttons';
import { Input } from '@/lib/Input';
import { Label } from '@/lib/Label';
import { TextArea } from '@/lib/TextArea';
import { RootState } from '@/reducers';
import { useSendSupportEmailMutation } from '@/services/endpoints/support/endpoints';
import { useFormik } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

type SupportForm = { onConfirm: VoidFunction };

export const SupportForm: FC<SupportForm> = ({ onConfirm }) => {
  const { t } = useTranslation('support');
  const { t: tErrors } = useTranslation('errors');

  const workspace = useSelector((state: RootState) => state.workspaces.currentWorkspace);

  const [sendEmailToSupport] = useSendSupportEmailMutation();

  const { values, errors, handleSubmit, handleChange, isSubmitting } = useFormik({
    initialValues: {
      email: '',
      message: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email(tErrors('invalidEmail')).required(tErrors('required')),
      message: Yup.string().min(5, tErrors('tooShort')).max(500, tErrors('tooLong')).required(tErrors('required')),
    }),
    onSubmit: async (values) => {
      await sendEmailToSupport({
        ...values,
        workspaceId: workspace?.id,
      });

      onConfirm();
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  return (
    <form onSubmit={handleSubmit} className='flex max-w-[352px] grow flex-col gap-4'>
      <div>
        <Label className='font-medium'>{t('modal.emailLabel')}</Label>
        <Input
          id='email'
          type='email'
          value={values.email}
          onChange={handleChange}
          errorMsg={errors.email}
          className='placeholder-shown:capitalize'
          placeholder={t('modal.emailPlaceholder')}
        />
      </div>

      <div className='grow'>
        <Label className='font-medium'>{t('modal.requestLabel')}</Label>
        <TextArea
          id='message'
          value={values.message}
          onChange={handleChange}
          errorMsg={errors.message}
          className='max-h-40 min-h-[6.25rem]'
          wrapperClassName='m-0 text-left flex-grow'
          placeholder={t('modal.requestPlaceholder')}
          maxLength={500}
        />
      </div>
      <Button size='lg' className='w-full capitalize' type='submit' isLoading={isSubmitting}>
        {t('send')}
      </Button>
    </form>
  );
};
